import { useEffect, useRef, useState } from 'react'

import { useLocalParticipant, useMediaDeviceSelect } from '@livekit/components-react'

import { AudioRecorder, AudioRecorderConfig } from 'modules/meetings/lib/utils/audioRecorder'

export interface UseLocalAudioRecordingOptions {
  /** Called every time a chunk is ready. */
  onAudioChunk: (blob: Blob) => Promise<void>
  /** Maximum length of any single chunk, in seconds. Defaults to 15 s. */
  maxChunkDuration?: number
  /** Minimum length of a chunk that can be cut early because of silence, in seconds. Defaults to 2 s. */
  minChunkDuration?: number
  /** Number of consecutive seconds below `silenceThresholdLow` that counts as “prolonged silence”. Defaults to 1 s. */
  minSilenceDuration?: number
  /** Root-mean-square level considered silence / speech. Defaults ≈ 0.01 (low) ↔ 0.02 (high). */
  silenceThresholdLow?: number
  silenceThresholdHigh?: number
}

export function useLocalAudioRecording({
  onAudioChunk,
  maxChunkDuration = 15,
  minChunkDuration = 2,
  minSilenceDuration = 1,
  silenceThresholdLow = 0.01,
  silenceThresholdHigh = 0.02,
}: UseLocalAudioRecordingOptions) {
  const { localParticipant } = useLocalParticipant()
  const recorderRef = useRef<AudioRecorder | null>(null)
  const [isRecording, setIsRecording] = useState(false)

  /* For displaying in UI exactly the same as before */
  const participantIdentity = localParticipant.identity

  const {
    devices: _audioInputDevices,
    activeDeviceId: activeAudioInputId,
    setActiveMediaDevice: _setActiveAudioDevice,
  } = useMediaDeviceSelect({ kind: 'audioinput' })

  useEffect(() => {
    let cancelled = false
    let stream: MediaStream | null = null

    async function start(): Promise<void> {
      /* ------------------------------------------------------------ */
      /* 1. Obtain a MediaStream                                      */
      /* ------------------------------------------------------------ */
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          deviceId: activeAudioInputId,
        },
      })

      if (cancelled || !stream) return

      /* ------------------------------------------------------------ */
      /* 2. Configure and start AudioRecorder                         */
      /* ------------------------------------------------------------ */
      const config: AudioRecorderConfig = {
        maxChunkDuration,
        minChunkDuration,
        minSilenceDuration,
        silenceThresholdLow,
        silenceThresholdHigh,
      }

      const recorder = new AudioRecorder(config)
      recorderRef.current = recorder

      recorder.onChunk = async (chunk) => {
        try {
          await onAudioChunk(chunk.blob)
        } catch {
          /* swallow errors (this will avoid breaking the rest of meetings) */
        }
      }

      recorder.onError = () => {
        /* silently ignore (this will avoid breaking the rest of meetings) */
      }

      try {
        await recorder.initialize(stream)
        await recorder.startRecording(maxChunkDuration)
        if (cancelled) {
          await recorder.stopRecording().catch(() => void 0)
          recorder.cleanup()
          return
        }
        setIsRecording(true)
      } catch {
        /* ignore errors (this will avoid breaking the rest of meetings) */
      }
    }

    start()

    return () => {
      cancelled = true
      const recorder = recorderRef.current
      if (recorder) {
        recorder.stopRecording().catch(() => void 0)
        recorder.cleanup()
      }
      if (stream) {
        stream.getTracks().forEach((t) => t.stop())
      }
      setIsRecording(false)
    }
  }, [
    localParticipant,
    localParticipant.audioTrackPublications.size,
    onAudioChunk,
    maxChunkDuration,
    minChunkDuration,
    minSilenceDuration,
    silenceThresholdLow,
    silenceThresholdHigh,
    activeAudioInputId,
  ])

  return { isRecording, participantIdentity }
}
